import { useState, useEffect } from "react";
import {
  adminGetAllBusiness,
  adminUpdateBusinessVisibility,
  adminUpsertIntegration,
  adminGetIntegrations,
  adminGetOwners,
  adminAddOwner,
  adminRemoveOwner,
  adminGrantStudioAccess,
} from "../../../../api/apiEndpoints";
import InfoTip from "../../../../components/PartnerDashboard/InfoTip";
import MultiSelect from "../MultiSelect";
import {
  SectionTitle, Card, Row, Input, Select, Button, Table, Th, Td, Tr, Badge, FormGrid, Label, FormField, StatusText, ModalOverlay, Modal, ModalHeader, ModalTitle, CloseButton, EmptyState, StatCards, StatCard, StatValue, StatLabel,
} from "../styles";

const ROLE_HELP = [
  "Owner: everything, including revenue, reports, payouts, class drop settings, member exports, and adding or removing staff.",
  "Manager: everything an owner can do except managing staff.",
  "Front desk: members, check-ins, expiring members and upgrade opportunities. No revenue, reports, payouts, class drops, staff or member exports.",
].join("\n\n");

const BLANK_INTEGRATION = {
  provider: "mindbody" as "mindbody" | "marianatek" | "other",
  externalBusinessId: "",
  externalLocationId: "",
  apiBaseUrl: "",
  jrBookingServiceId: "",
  isActive: true,
};

const GymsPage = () => {
  const [gyms, setGyms] = useState<any[]>([]);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantRole, setGrantRole] = useState<"owner" | "manager" | "frontDesk">("owner");
  const [grantStudios, setGrantStudios] = useState<string[]>([]);
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantStatus, setGrantStatus] = useState<{ msg: string; ok: boolean } | null>(null);
  const [loading, setLoading] = useState(false);
  const [visibilityLoading, setVisibilityLoading] = useState<string | null>(null);
  const [selectedGym, setSelectedGym] = useState<any | null>(null);
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [intLoading, setIntLoading] = useState(false);
  const [intForm, setIntForm] = useState(BLANK_INTEGRATION);
  const [intStatus, setIntStatus] = useState<{ msg: string; ok: boolean } | null>(null);
  const [intSaving, setIntSaving] = useState(false);

  // Owners
  const [owners, setOwners] = useState<any[]>([]);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerStatus, setOwnerStatus] = useState<{ msg: string; ok: boolean } | null>(null);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [removeOwnerLoading, setRemoveOwnerLoading] = useState<string | null>(null);

  const fetchGyms = async () => {
    setLoading(true);
    try {
      const res = await adminGetAllBusiness();
      setGyms((res.data as any).businesses ?? []);
    } catch {
      setGyms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGyms(); }, []);

  const toggleVisibility = async (gym: any) => {
    setVisibilityLoading(gym.businessId);
    try {
      await adminUpdateBusinessVisibility(gym.businessId, !gym.hide);
      setGyms((prev) =>
        prev.map((g) => g.businessId === gym.businessId ? { ...g, hide: !g.hide } : g)
      );
    } catch (e: any) {
      alert(e?.response?.data?.message || "Failed to update visibility.");
    } finally {
      setVisibilityLoading(null);
    }
  };

  const openIntegrations = async (gym: any) => {
    setSelectedGym(gym);
    setIntStatus(null);
    setOwnerStatus(null);
    setOwnerEmail("");
    setIntForm(BLANK_INTEGRATION);
    await loadGymDetails(gym);
  };

  const loadGymDetails = async (gym: any) => {
    setIntLoading(true);
    try {
      const [intRes, ownersRes] = await Promise.all([
        adminGetIntegrations(gym.businessId),
        adminGetOwners(gym.businessId),
      ]);
      const rows: any[] = (intRes.data as any).integrations ?? [];
      setIntegrations(rows);
      setOwners((ownersRes.data as any).owners ?? []);
      if (rows.length > 0) {
        const r = rows[0];
        setIntForm({
          provider: r.provider ?? "mindbody",
          externalBusinessId: r.external_business_id ?? "",
          externalLocationId: r.external_location_id ?? "",
          apiBaseUrl: r.api_base_url ?? "",
          jrBookingServiceId: r.jr_booking_service_id ?? "",
          isActive: r.is_active ?? false,
        });
      }
    } catch {
      setIntegrations([]);
      setOwners([]);
    } finally {
      setIntLoading(false);
    }
  };

  const handleAddOwner = async () => {
    if (!selectedGym || !ownerEmail) return;
    setOwnerLoading(true);
    setOwnerStatus(null);
    try {
      const res = await adminAddOwner(selectedGym.businessId, ownerEmail);
      const data = res.data as any;
      if (data.invited) {
        setOwnerStatus({ msg: `No account found — invite sent to ${ownerEmail}. They'll be added once they create their account.`, ok: true });
      } else {
        setOwners(data.owners ?? []);
        setOwnerStatus({ msg: `${ownerEmail} added as an owner.`, ok: true });
      }
      setOwnerEmail("");
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || "Failed to add owner.";
      setOwnerStatus({ msg, ok: false });
    } finally {
      setOwnerLoading(false);
    }
  };

  const handleRemoveOwner = async (userId: string) => {
    if (!selectedGym) return;
    const owner = owners.find((o: any) => o.userId === userId);
    if (!window.confirm(`Remove ${owner?.email || "this owner"} from ${selectedGym.name}? They'll lose access to its dashboard.`)) return;
    setRemoveOwnerLoading(userId);
    try {
      const res = await adminRemoveOwner(selectedGym.businessId, userId);
      setOwners((res.data as any).owners ?? []);
    } catch (e: any) {
      alert(e?.response?.data?.message || "Failed to remove owner.");
    } finally {
      setRemoveOwnerLoading(null);
    }
  };

  const saveIntegration = async () => {
    if (!selectedGym) return;
    setIntSaving(true);
    setIntStatus(null);
    try {
      await adminUpsertIntegration(selectedGym.businessId, {
        provider: intForm.provider,
        externalBusinessId: intForm.externalBusinessId || undefined,
        externalLocationId: intForm.externalLocationId || undefined,
        apiBaseUrl: intForm.apiBaseUrl || undefined,
        jrBookingServiceId: intForm.jrBookingServiceId || undefined,
        isActive: intForm.isActive,
      });
      await loadGymDetails(selectedGym);
      setIntStatus({ msg: "Integration saved.", ok: true });
    } catch (e: any) {
      setIntStatus({ msg: e?.response?.data?.message || "Failed to save.", ok: false });
    } finally {
      setIntSaving(false);
    }
  };

  const [gymSearch, setGymSearch] = useState("");
  const hidden = gyms.filter((g) => g.hide);
  const live = gyms.filter((g) => !g.hide);
  const filteredGyms = gymSearch
    ? gyms.filter((g) =>
        (g.name || "").toLowerCase().includes(gymSearch.toLowerCase()) ||
        (g.address?.fullAddress || "").toLowerCase().includes(gymSearch.toLowerCase())
      )
    : gyms;

  const grantAccess = async () => {
    setGrantLoading(true);
    setGrantStatus(null);
    try {
      const res = await adminGrantStudioAccess({
        email: grantEmail.trim(),
        permissionRole: grantRole,
        businessIds: grantStudios,
      });
      const d = res.data as any;
      const n = grantStudios.length;
      const studios = `${n} studio${n === 1 ? "" : "s"}`;
      setGrantStatus({
        ok: true,
        msg: d.invited
          ? `No account yet, so we emailed ${grantEmail.trim()} one invite. They'll get access to all ${studios} when they create their account.`
          : `Done. ${grantEmail.trim()} now has access to ${studios}${d.alreadyHadAccess ? ` (${d.alreadyHadAccess} they already had)` : ""}.`,
      });
      setGrantEmail("");
      setGrantStudios([]);
    } catch (e: any) {
      setGrantStatus({ ok: false, msg: e?.response?.data?.error || e?.response?.data?.message || "Couldn't give access." });
    } finally {
      setGrantLoading(false);
    }
  };

  return (
    <>
      <StatCards>
        <StatCard><StatValue>{gyms.length}</StatValue><StatLabel>Total Gyms</StatLabel></StatCard>
        <StatCard><StatValue>{live.length}</StatValue><StatLabel>Live</StatLabel></StatCard>
        <StatCard><StatValue>{hidden.length}</StatValue><StatLabel>Hidden</StatLabel></StatCard>
      </StatCards>

      <Card>
        <SectionTitle>Give someone access to studios</SectionTitle>
        <FormGrid>
          <FormField>
            <Label>Email</Label>
            <Input type="email" placeholder="manager@studio.com" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} />
          </FormField>
          <FormField>
            <Label style={{ display: "flex", alignItems: "center", gap: 2 }}>
              Role
              <InfoTip label="About roles" text={ROLE_HELP} />
            </Label>
            <Select value={grantRole} onChange={(e) => setGrantRole(e.target.value as typeof grantRole)}>
              <option value="owner">Owner</option>
              <option value="manager">Manager</option>
              <option value="frontDesk">Front desk</option>
            </Select>
          </FormField>
        </FormGrid>
        <FormField style={{ marginTop: 12 }}>
          <Label>Studios</Label>
          <MultiSelect
            options={[...gyms]
              .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
              .map((g) => ({ value: g.businessId, label: g.name ?? "Unnamed studio" }))}
            selected={grantStudios}
            onChange={setGrantStudios}
            allLabel="Select studios…"
            noun="studios"
          />
        </FormField>
        <Row style={{ marginTop: 12, marginBottom: 0 }}>
          <Button onClick={grantAccess} disabled={grantLoading || !grantEmail.trim() || grantStudios.length === 0}>
            {grantLoading ? "Saving…" : `Give access${grantStudios.length ? ` to ${grantStudios.length} studio${grantStudios.length === 1 ? "" : "s"}` : ""}`}
          </Button>
        </Row>
        {grantStatus && <StatusText success={grantStatus.ok}>{grantStatus.msg}</StatusText>}
      </Card>

      <Card>
        <SectionTitle>All Gyms</SectionTitle>
        <div style={{ marginBottom: 16, maxWidth: 480 }}>
          <Input
            placeholder="Search by name or address…"
            value={gymSearch}
            onChange={(e) => setGymSearch(e.target.value)}
          />
        </div>
        {loading ? (
          <EmptyState>Loading…</EmptyState>
        ) : filteredGyms.length === 0 ? (
          <EmptyState>{gymSearch ? `No gyms match "${gymSearch}"` : "No gyms found."}</EmptyState>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table>
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Address</Th>
                  <Th>Products</Th>
                  <Th>Status</Th>
                  <Th>Visibility</Th>
                  <Th>Integrations</Th>
                </tr>
              </thead>
              <tbody>
                {filteredGyms.map((g) => (
                  <Tr key={g.businessId} onClick={() => openIntegrations(g)}>
                    <Td><strong>{g.name}</strong></Td>
                    <Td data-label="Address" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {g.address?.fullAddress || "—"}
                    </Td>
                    <Td data-label="Products">{(g.products ?? []).length} products</Td>
                    <Td data-label="Status">
                      {g.isClosed
                        ? <Badge color="#fee2e2">Closed</Badge>
                        : <Badge color="#dcfce7">Open</Badge>}
                    </Td>
                    <Td data-label="Visibility" onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Badge color={g.hide ? "#fef3c7" : "#dcfce7"}>
                          {g.hide ? "Hidden" : "Live"}
                        </Badge>
                        <Button
                          variant={g.hide ? "primary" : "secondary"}
                          disabled={visibilityLoading === g.businessId}
                          onClick={() => toggleVisibility(g)}
                          style={{ padding: "4px 10px", fontSize: 11 }}
                        >
                          {visibilityLoading === g.businessId
                            ? "…"
                            : g.hide ? "Go Live" : "Hide"}
                        </Button>
                      </div>
                    </Td>
                    <Td data-label="Integrations">
                      <Button
                        variant="secondary"
                        onClick={(e) => { e.stopPropagation(); openIntegrations(g); }}
                        style={{ padding: "4px 10px", fontSize: 11 }}
                      >
                        Manage
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      {selectedGym && (
        <ModalOverlay onClick={() => setSelectedGym(null)}>
          <Modal onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <ModalHeader>
              <ModalTitle>Manage — {selectedGym.name}</ModalTitle>
              <CloseButton onClick={() => setSelectedGym(null)}>×</CloseButton>
            </ModalHeader>

            {intLoading ? (
              <EmptyState>Loading…</EmptyState>
            ) : (
              <>
                <FormGrid>
                  <FormField>
                    <Label>Provider</Label>
                    <Select
                      value={intForm.provider}
                      onChange={(e) => setIntForm({ ...intForm, provider: e.target.value as any })}
                    >
                      <option value="mindbody">MindBody</option>
                      <option value="marianatek">MarianaTek</option>
                      <option value="other">Other</option>
                    </Select>
                  </FormField>
                  <FormField>
                    <Label>Site / Business ID</Label>
                    <Input
                      placeholder="e.g. 12345"
                      value={intForm.externalBusinessId}
                      onChange={(e) => setIntForm({ ...intForm, externalBusinessId: e.target.value })}
                    />
                  </FormField>
                  <FormField>
                    <Label>Location ID</Label>
                    <Input
                      placeholder="e.g. 1"
                      value={intForm.externalLocationId}
                      onChange={(e) => setIntForm({ ...intForm, externalLocationId: e.target.value })}
                    />
                  </FormField>
                  {intForm.provider === "marianatek" && (
                    <FormField>
                      <Label>API Base URL</Label>
                      <Input
                        placeholder="https://gym.marianatek.com/api"
                        value={intForm.apiBaseUrl}
                        onChange={(e) => setIntForm({ ...intForm, apiBaseUrl: e.target.value })}
                      />
                    </FormField>
                  )}
                  <FormField>
                    <Label>JR Booking Service ID</Label>
                    <Input
                      placeholder="Optional"
                      value={intForm.jrBookingServiceId}
                      onChange={(e) => setIntForm({ ...intForm, jrBookingServiceId: e.target.value })}
                    />
                  </FormField>
                </FormGrid>
                <Row style={{ marginTop: 12, gap: 24, alignItems: "center" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={intForm.isActive}
                      onChange={(e) => setIntForm({ ...intForm, isActive: e.target.checked })}
                    />
                    Active (enables booking on the app)
                  </label>
                </Row>
                <Row style={{ marginTop: 16 }}>
                  <Button onClick={saveIntegration} disabled={intSaving}>
                    {intSaving ? "Saving…" : "Save Integration"}
                  </Button>
                  <Button variant="secondary" onClick={() => setSelectedGym(null)}>Cancel</Button>
                </Row>
                {intStatus && <StatusText success={intStatus.ok}>{intStatus.msg}</StatusText>}

                {/* ── Owners ──────────────────────────────────────────── */}
                <SectionTitle style={{ fontSize: 13, marginTop: 28, marginBottom: 10 }}>Owners</SectionTitle>
                <p style={{ fontSize: 12, color: "#888", margin: "0 0 12px" }}>
                  Owners can log in and view this gym's dashboard. Add by partner account email.
                </p>

                {owners.length > 0 && (
                  <Table style={{ marginBottom: 14 }}>
                    <thead>
                      <tr>
                        <Th>Name</Th>
                        <Th>Email</Th>
                        <Th></Th>
                      </tr>
                    </thead>
                    <tbody>
                      {owners.map((o: any) => (
                        <tr key={o.userId}>
                          <Td>{o.firstName} {o.lastName}</Td>
                          <Td data-label="Email" style={{ fontSize: 12, color: "#666" }}>{o.email}</Td>
                          <Td>
                            <Button
                              variant="danger"
                              onClick={() => handleRemoveOwner(o.userId)}
                              disabled={removeOwnerLoading === o.userId}
                              style={{ padding: "3px 10px", fontSize: 11 }}
                            >
                              {removeOwnerLoading === o.userId ? "…" : "Remove"}
                            </Button>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}

                <Row style={{ marginBottom: 4 }}>
                  <Input
                    placeholder="partner@gym.com"
                    type="email"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <Button onClick={handleAddOwner} disabled={!ownerEmail || ownerLoading}>
                    {ownerLoading ? "Adding…" : "Add Owner"}
                  </Button>
                </Row>
                {ownerStatus && <StatusText success={ownerStatus.ok}>{ownerStatus.msg}</StatusText>}

                {integrations.length > 0 && (
                  <>
                    <SectionTitle style={{ fontSize: 13, marginTop: 24, marginBottom: 8 }}>
                      Existing records
                    </SectionTitle>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Provider</Th>
                          <Th>Site ID</Th>
                          <Th>Location ID</Th>
                          <Th>Active</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {integrations.map((r: any) => (
                          <tr key={r.id}>
                            <Td>{r.provider}</Td>
                            <Td data-label="Site ID">{r.external_business_id || "—"}</Td>
                            <Td data-label="Location ID">{r.external_location_id || "—"}</Td>
                            <Td data-label="Active">
                              <Badge color={r.is_active ? "#dcfce7" : "#f3f4f6"}>
                                {r.is_active ? "Yes" : "No"}
                              </Badge>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </>
                )}
              </>
            )}
          </Modal>
        </ModalOverlay>
      )}
    </>
  );
};

export default GymsPage;
